import React from 'react';
import { FormattedMessage } from 'react-intl';
import { SRP_TYPES } from './SRPTypes.definitions';
import { getFullPagePath } from '../appRoutes';
import { ARD_PERMA_SEARCH, ARD_SEARCH } from '../appRoutes.definitions';
import CategoryStructuredData from '../../components/SearchResultsPage/CategoryStructuredData';

const getPagePrefix = (intl, page) => {
  if (page < 2) return '';
  return intl.formatMessage(
    { id: 'search.headings.pagePrefix', defaultMessage: 'Page {page} - ' },
    {
      page: page,
    }
  );
};

export const SRPSEODataStrategies = {
  [SRP_TYPES.DEFAULT]: (intl, searchData, page, categoryFilter, categoryPath, filters, searchTerm) => {
    const category = categoryFilter?.[categoryFilter.length - 1];

    const pagePrefix = getPagePrefix(intl, page);
    const allProducts = intl.formatMessage({
      id: 'common.allProducts',
      defaultMessage: 'All products',
    });
    const allCategories = intl.formatMessage({
      id: 'common.allCategories',
      defaultMessage: 'all categories',
    });

    const titleTerm = searchTerm ? `'${searchTerm}'` : allProducts;
    const titleCategory = category || allCategories;

    const getSRPCanonical = () => {
      if (!categoryPath) return getFullPagePath(ARD_SEARCH);

      const searchParams = new URLSearchParams();
      if (page && page > 1) searchParams.set('page', page);
      const pageCanonical = new URL(getFullPagePath(ARD_SEARCH, categoryPath));
      pageCanonical.search = searchParams.toString();

      return pageCanonical.toString();
    };

    const canonicalUrl = getSRPCanonical();
    const categorySchemaRender = ({ products }) => (
      <CategoryStructuredData categoryName={category} categoryUrl={canonicalUrl} products={products} />
    );
    const categorySchema = category ? categorySchemaRender : null;
    return {
      metaTitle: intl.formatMessage(
        { id: 'search.headings.metaTitle', defaultMessage: '{page} {term} in {category}' },
        {
          page: pagePrefix,
          term: titleTerm,
          category: titleCategory,
        }
      ),
      canonicalUrl,
      headingH1: (
        <FormattedMessage
          defaultMessage="{page}<b>'{term}'</b> in {category}"
          id="search.headings.title"
          values={{
            page: pagePrefix,
            term: titleTerm,
            category: titleCategory,
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      ),
      schemaGraph: categorySchema,
    };
  },
  [SRP_TYPES.API_OVERRIDE]: (intl, searchData, page, categoryFilter, categoryPath, filters, searchTerm) => {
    //Described in: https://tradetracker.atlassian.net/browse/MIAB-3586

    const pageTitleOverride = searchData?.page_title?.trim();
    const capitalise = (text) => text[0].toUpperCase() + text.slice(1);
    const pageTitleOverrideCapitalised = pageTitleOverride && capitalise(pageTitleOverride);
    //searchData?.canonical param is present in search api response only when parameters are presented as id's
    //at this point this is the case only with perma search links like /n/some-text/435435+664456
    //this is aplicable for API_OVERRIDE srp type
    const permaSearchCanonical = searchData?.canonical;
    return {
      metaTitle: pageTitleOverrideCapitalised,
      canonicalUrl: getFullPagePath(ARD_PERMA_SEARCH, permaSearchCanonical[0], permaSearchCanonical[1]),
      headingH1: pageTitleOverrideCapitalised,
    };
  },
  [SRP_TYPES.BRAND]: (intl, searchData, page, categoryFilter, categoryPath, filters) => {
    const searchTermOverride = searchData?.page_title?.trim();
    const category = searchTermOverride ? '' : categoryFilter?.[categoryFilter.length - 1];
    const allCategories = intl.formatMessage({
      id: 'common.allCategories',
      defaultMessage: 'all categories',
    });
    const titleCategory = category || allCategories;
    const isSingleBrandSelected = Array.isArray(filters?.brands) && filters?.brands.length === 1;
    const singleBrandName = isSingleBrandSelected ? filters?.brands[0] : '';

    const getBrandSRPCanonical = () => {
      const searchParams = new URLSearchParams();
      if (page && page > 1) searchParams.set('page', page);
      searchParams.set('brand', singleBrandName);

      const pageCanonical = new URL(getFullPagePath(ARD_SEARCH, categoryPath));
      pageCanonical.search = searchParams.toString();
      return pageCanonical.toString();
    };

    const canonicalUrl = getBrandSRPCanonical();
    const brandCanonicalUrl = getFullPagePath(ARD_SEARCH) + '?brand=' + singleBrandName;

    return {
      metaTitle: `${singleBrandName} ${titleCategory}`,
      canonicalUrl,
      headingH1: (
        <>
          <b>{singleBrandName}</b> {titleCategory}
        </>
      ),
      schemaGraph: ({ products }) => (
        <CategoryStructuredData
          categoryName={category}
          categoryUrl={canonicalUrl}
          products={products}
          brandOverride={singleBrandName}
          brandCanonicalUrl={brandCanonicalUrl}
        />
      ),
    };
  },
  [SRP_TYPES.DISCOUNTS]: (intl, searchData, page) => {
    const pagePrefix = getPagePrefix(intl, page);

    const metaTitle = pagePrefix + intl.formatMessage({ id: 'search.discounts', defaultMessage: 'Discounts' });
    return {
      metaTitle,
      canonicalUrl: getFullPagePath(ARD_SEARCH),
      headingH1: (
        <>
          {pagePrefix}
          <FormattedMessage defaultMessage="Discounts" id="search.discounts" />
        </>
      ),
    };
  },
};
